import React, { useState, useEffect, useMemo } from 'react'
import SearchBar from './search-bar'
import { makeStyles } from '@material-ui/styles'
import { useIsTablet } from 'lib/hooks'
import StudentItemView from './student-item-view'
import { parseStudents } from './utils'
import EnhancedTable from 'components/tables/enhanced-table'
import StudentsTablePagination from './students-table-pagination'
import PermContactCalendarOutlinedIcon from 'components/material-symbol/icons/PermContactCalendar'
import SectionEmptyState from 'components/section-empty-state'
import ArrowDropDownIcon from 'components/material-symbol/icons/ArrowDropDown'
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Popover,
  Radio,
  RadioGroup,
  TableContainer,
} from '@material-ui/core'
import { onReschedule, onPayment, onCancellation } from 'lib/utils'
import {
  sortByAgeGroup,
  sortByFullName,
  sortByUpcoming,
} from 'lib/student-list/utils'
import SwapVertIcon from 'components/material-symbol/icons/SwapVert'

const AGE_FILTER_OPTIONS = [
  { value: 'all', label: 'All Ages' },
  { value: 'adult', label: 'Adult' },
  { value: 'junior', label: 'Junior' },
]

export const useStyles = makeStyles(theme => ({
  containerStyles: {
    boxSizing: 'border-box',
    padding: theme.spacing(3, 4.5, 2),
    maxWidth: '100%',
    [theme.breakpoints.down('sm')]: {
      marginBottom: '60px',
      padding: theme.spacing(3, 1, 2),
    },
  },
  tableShell: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    backgroundColor: theme.palette.common.white,
    overflow: 'hidden',
  },
  filterButton: {
    height: 40,
    borderRadius: 8,
    fontWeight: 500,
    flexShrink: 0,
    borderColor: '#C4C7C9',
    color: '#172024',
    '& .MuiButton-endIcon': {
      color: '#6B7C87',
    },
  },
  filterButtonActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: '#E0EAFF',
    color: '#001C57',
    fontWeight: 600,
    '& .MuiButton-endIcon': {
      color: '#001C57',
    },
    '&:hover': {
      backgroundColor: '#D4E2FF',
      borderColor: theme.palette.primary.main,
    },
  },
  filterMenuPaper: {
    marginTop: theme.spacing(0.5),
    minWidth: 200,
  },
  filterMenuContent: {
    padding: theme.spacing(1.5, 2),
    boxSizing: 'border-box',
  },
  filterForm: {
    margin: 0,
    padding: 0,
    border: 0,
    minWidth: 0,
  },
  filterLegend: {
    display: 'block',
    fontSize: '0.6875rem',
    fontWeight: 600,
    lineHeight: 1.3,
    color: '#6B7C87',
    padding: 0,
    margin: 0,
    marginBottom: theme.spacing(1),
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  filterRadioGroup: {
    padding: 0,
    margin: 0,
  },
  filterRadioLabel: {
    margin: 0,
    width: '100%',
    minHeight: 40,
    marginLeft: 0,
    marginRight: 0,
    '& .MuiFormControlLabel-label': {
      fontSize: '0.875rem',
      lineHeight: '20px',
      color: '#172024',
    },
    '& .MuiRadio-root': {
      color: '#40484C',
      padding: theme.spacing(1),
    },
    '& .MuiRadio-colorPrimary.Mui-checked': {
      color: '#0053FF',
    },
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    width: '100%',
    boxSizing: 'border-box',
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
    },
  },
  searchGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flex: '1 1 0',
    minWidth: 0,
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
      width: '100%',
    },
  },
  searchField: {
    flex: '1 1 0',
    minWidth: 0,
    maxWidth: 360,
    width: '100%',
  },
  toolbarActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flexShrink: 0,
    marginLeft: 'auto',
    [theme.breakpoints.down('sm')]: {
      marginLeft: 'auto',
    },
  },
  tableContent: {
    padding: theme.spacing(0, 1, 1),
  },
  listEmptyStateContent: {
    padding: theme.spacing(4, 2),
  },
}))

export default function StudentListView({
  students,
  sessions,
  refetchStudents,
  refetchSessions,
}) {
  const classes = useStyles()
  const isTablet = useIsTablet()
  const searchableItems = useMemo(() => parseStudents(students, sessions), [
    students,
    sessions,
  ])
  const [filteredStudents, setFilteredStudents] = useState(searchableItems)
  const [searchTerm, setSearchTerm] = useState('')
  const [ageGroup, setAgeGroup] = useState('all')
  const [filterAnchorEl, setFilterAnchorEl] = useState(null)
  const [page, setPage] = useState(0)
  const rowsPerPage = 25

  // returns true/false for if student is in selected age group
  const isInAgeGroup = ({ isMinor }, desiredGroup) => {
    if (desiredGroup === 'all') {
      return true
    } else if (isMinor && desiredGroup === 'junior') {
      return true
    } else if (!isMinor && desiredGroup === 'adult') {
      return true
    } else {
      return false
    }
  }

  // true if search term is empty - '' - or contained by name; false otherwise
  const nameMatchesSearchToken = ({ firstName, lastName }, token) => {
    const fullName = `${firstName} ${lastName}`.toLowerCase().trim()
    const term = token.toLowerCase().trim()

    return fullName.includes(term)
  }

  // inside the call to searchableItems.filter, return cond && otherCond && ... for filtering purposes
  //
  // In doing this we can compose filters.
  //
  // Make sure to update the effect dependencies list.
  useEffect(() => {
    const filtered = searchableItems.filter(item => {
      return (
        nameMatchesSearchToken(item, searchTerm) && isInAgeGroup(item, ageGroup)
      )
    })
    setFilteredStudents(filtered)
    setPage(0)
  }, [searchTerm, searchableItems, ageGroup])

  const handleAgeGroupSelect = value => {
    setAgeGroup(value)
  }

  const filterMenuOpen = Boolean(filterAnchorEl)
  const hasActiveAgeFilter = ageGroup !== 'all'
  const activeAgeGroupLabel =
    AGE_FILTER_OPTIONS.find(option => option.value === ageGroup)?.label ||
    'Filters'

  const filterButtons = (
    <>
      <Button
        variant="outlined"
        color="default"
        className={`${classes.filterButton} ${
          hasActiveAgeFilter ? classes.filterButtonActive : ''
        }`}
        aria-controls="student-filters-menu"
        aria-haspopup="true"
        aria-expanded={filterMenuOpen}
        aria-label={
          hasActiveAgeFilter
            ? `Student age group filter: ${activeAgeGroupLabel} selected`
            : 'Student age group filter'
        }
        onClick={event => setFilterAnchorEl(event.currentTarget)}
        endIcon={<ArrowDropDownIcon />}
      >
        {hasActiveAgeFilter ? activeAgeGroupLabel : 'Filters'}
      </Button>
      <Popover
        id="student-filters-menu"
        open={filterMenuOpen}
        anchorEl={filterAnchorEl}
        onClose={() => setFilterAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          className: classes.filterMenuPaper,
          style: filterAnchorEl
            ? { minWidth: filterAnchorEl.clientWidth }
            : undefined,
        }}
      >
        <Box className={classes.filterMenuContent}>
          <FormControl
            component="fieldset"
            fullWidth
            className={classes.filterForm}
          >
            <FormLabel component="legend" className={classes.filterLegend}>
              Age Group
            </FormLabel>
            <RadioGroup
              aria-label="Age group filter"
              name="age-group-filter"
              className={classes.filterRadioGroup}
              value={ageGroup}
              onChange={event => {
                handleAgeGroupSelect(event.target.value)
                setFilterAnchorEl(null)
              }}
            >
              {AGE_FILTER_OPTIONS.map(option => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  className={classes.filterRadioLabel}
                  control={<Radio color="primary" size="small" />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Box>
      </Popover>
    </>
  )

  const columns = [
    {
      id: 'name',
      label: 'Name',
      align: 'left',
      width: isTablet ? '50%' : '30%',
      mobile: true,
      sortable: true,
      sortComparator: sortByFullName,
    },
    {
      id: 'ageGroup',
      label: 'Age Group',
      align: 'left',
      width: isTablet ? '30%' : '12%',
      mobile: true,
      sortable: true,
      sortComparator: sortByAgeGroup,
      inactiveSortIcon: SwapVertIcon,
    },
    {
      id: 'upcoming',
      label: 'Next Upcoming Session',
      align: 'left',
      width: '38%',
      mobile: false,
      sortable: true,
      sortComparator: sortByUpcoming,
      inactiveSortIcon: SwapVertIcon,
    },
    {
      id: 'actions',
      label: '',
      align: 'right',
      width: isTablet ? '20%' : '10%',
      mobile: true,
      sortable: false,
    },
  ]

  const pageCount =
    Math.ceil((filteredStudents?.length || 0) / rowsPerPage) || 1

  const rows = filteredStudents?.map(student => (
    <StudentItemView
      student={student}
      refetchStudents={refetchStudents}
      key={student.id}
      onReschedule={() => onReschedule(refetchSessions)}
      onPayment={() => onPayment(refetchSessions)}
      onCancellation={() => onCancellation(refetchSessions)}
    />
  ))

  return (
    <TableContainer className={classes.containerStyles}>
      <Box className={classes.tableShell}>
        <Box className={classes.toolbar}>
          <Box className={classes.searchGroup}>
            <SearchBar
              className={classes.searchField}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              placeholder="Search Students"
              data-testid="student-list-search"
              style={{ margin: 0 }}
            />
            {filterButtons}
          </Box>
          <Box className={classes.toolbarActions}>
            {filteredStudents.length > 0 && (
              <StudentsTablePagination
                page={page}
                pageCount={pageCount}
                rowsPerPage={rowsPerPage}
                totalCount={filteredStudents.length}
                onPageChange={setPage}
              />
            )}
          </Box>
        </Box>
        <Box className={classes.tableContent}>
          <EnhancedTable
            rows={rows}
            columns={columns}
            label="Students"
            numberOfRows={rowsPerPage}
            variant="list"
            rowHeight="56px"
            initialOrderBy="name"
            initialOrder="asc"
            page={page}
            onPageChange={setPage}
            showHeaderPagination={false}
            showPaginator={false}
            emptyState={() => (
              <Box className={classes.listEmptyStateContent}>
                <SectionEmptyState
                  Icon={PermContactCalendarOutlinedIcon}
                  title="Students"
                  caption="No matching students."
                  bordered={false}
                />
              </Box>
            )}
          />
        </Box>
      </Box>
    </TableContainer>
  )
}
